import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useVerifyResetOtpMutation,
  useRequestPasswordResetMutation,
} from "../services/appApi";
import "./ResetPasswordOtp.css";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

function ResetPasswordOtp() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";

  // Guard: redirect if no email in state
  useEffect(() => {
    if (!email) navigate("/forgot-password");
  }, [email, navigate]);

  // ── OTP digit state ──────────────────────────────────────────────────────
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);

  // ── API mutations ────────────────────────────────────────────────────────
  const [verifyResetOtp, { isLoading: isVerifying }] = useVerifyResetOtpMutation();
  const [requestReset, { isLoading: isResending }] = useRequestPasswordResetMutation();

  // ── UI state ─────────────────────────────────────────────────────────────
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [hasError, setHasError] = useState(false);

  // ── Timer — starts at 60 because an email was just sent ──────────────────
  const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // ── Digit handlers ───────────────────────────────────────────────────────
  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;

    const newDigits = [...digits];

    if (val.length > 1) {
      const pasted = val.slice(0, OTP_LENGTH);
      for (let i = 0; i < pasted.length; i++) {
        if (idx + i < OTP_LENGTH) newDigits[idx + i] = pasted[i];
      }
      setDigits(newDigits);
      inputRefs.current[Math.min(idx + pasted.length, OTP_LENGTH - 1)]?.focus();
      return;
    }

    newDigits[idx] = val;
    setDigits(newDigits);
    setHasError(false);
    setErrorMsg("");

    if (idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (digits[idx]) {
        newDigits[idx] = "";
        setDigits(newDigits);
      } else if (idx > 0) {
        newDigits[idx - 1] = "";
        setDigits(newDigits);
        inputRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;
    const newDigits = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < OTP_LENGTH; i++) newDigits[i] = text[i] || "";
    setDigits(newDigits);
    inputRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  // ── Auto-submit ──────────────────────────────────────────────────────────
  useEffect(() => {
    const allFilled = digits.every((d) => d !== "");
    if (!allFilled) return;
    const t = setTimeout(() => {
      document
        .getElementById("reset-otp-submit-btn")
        ?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }, 80);
    return () => clearTimeout(t);
  }, [digits]);

  // ── Verify handler ───────────────────────────────────────────────────────
  const handleVerify = useCallback(
    async (e) => {
      e.preventDefault();
      const otp = digits.join("");

      if (otp.length < OTP_LENGTH) {
        setErrorMsg("Please enter all 6 digits.");
        setHasError(true);
        return;
      }

      setErrorMsg("");
      setSuccessMsg("");

      const { data, error } = await verifyResetOtp({ email, otp });

      if (data?.success) {
        setSuccessMsg("✓ Code verified! Setting your new password…");
        setTimeout(() => {
          navigate("/reset-password", {
            state: { email, resetToken: data.resetToken },
          });
        }, 800);
      } else {
        const msg =
          error?.data?.error ||
          data?.error ||
          "Something went wrong. Please try again.";

        const fullMsg =
          error?.data?.expired
            ? msg + ' Click "Resend code" below to get a new one.'
            : msg;

        setErrorMsg(fullMsg);
        setHasError(true);

        setDigits(Array(OTP_LENGTH).fill(""));
        setTimeout(() => {
          inputRefs.current[0]?.focus();
          setHasError(false);
        }, 500);
      }
    },
    [digits, email, navigate, verifyResetOtp]
  );

  // ── Resend handler ───────────────────────────────────────────────────────
  const handleResend = async () => {
    if (timer > 0 || isResending) return;

    setErrorMsg("");
    setSuccessMsg("");

    const { data, error } = await requestReset({ email });

    if (data?.success) {
      setSuccessMsg("✓ A new code has been sent to your inbox.");
      setTimer(RESEND_COOLDOWN_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } else {
      const msg =
        error?.data?.error || "Could not resend. Please wait and try again.";
      setErrorMsg(msg);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="reset-otp-root">
      <div className="reset-otp-card">
        <div className="reset-otp-icon-wrap" aria-hidden="true">
          🔑
        </div>

        <h1 className="reset-otp-title">Enter reset code</h1>
        <p className="reset-otp-subtitle">
          We sent a 6-digit code to <strong>{email}</strong>.
          Enter it below to verify your identity.
        </p>

        {errorMsg && (
          <div className="reset-otp-error" role="alert">
            <span aria-hidden="true">⚠️</span> {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="reset-otp-success" role="status">
            {successMsg}
          </div>
        )}

        <form onSubmit={handleVerify} noValidate>
          <div className="otp-input-row" onPaste={handlePaste}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                id={`reset-otp-digit-${idx}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                autoFocus={idx === 0}
                autoComplete="one-time-code"
                className={[
                  "otp-digit",
                  d ? "otp-digit--filled" : "",
                  hasError ? "otp-digit--error" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onChange={(e) => handleChange(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                aria-label={`Digit ${idx + 1} of ${OTP_LENGTH}`}
              />
            ))}
          </div>

          <button
            id="reset-otp-submit-btn"
            type="submit"
            className="reset-otp-btn"
            disabled={isVerifying || digits.some((d) => !d)}
          >
            {isVerifying ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Verifying…
              </>
            ) : (
              "Verify code"
            )}
          </button>
        </form>

        <div className="resend-row">
          Didn't receive it?{" "}
          {timer > 0 ? (
            <span className="resend-cooldown">
              Resend in <span className="resend-countdown-value">{timer}s</span>
            </span>
          ) : (
            <button
              type="button"
              className="resend-btn"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? "Sending…" : "Resend code"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordOtp;
